<template>
  <div
    class="w-full min-h-0 flex flex-col px-4 pb-0! pt-[calc(env(safe-area-inset-top)+68px)] tablet-h:pt-[18px] tablet-h:pl-3 tablet-h:pr-[26px] relative"
    @click="handleClickOutside"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <AlertBanner class="mb-[15px] tablet-h:mb-[18px] tablet-h:ml-7" />

    <UpsellBanner
      v-if="billingStore.billing?.upsell"
      class="px-2 py-[7.5px] hidden tablet-h:flex"
    />

    <div
      class="absolute w-full h-full top-0 right-0 left-0 bottom-0 z-10 pointer-events-none"
      :class="overlayClasses"
    />

    <!-- Header -->
    <div class="tablet-h:ml-7 flex items-end justify-between mb-5">
      <div class="w-full">
        <h1
          class="tablet-h:hidden mb-[15px] text-[24px] font-medium leading-[30px] text-gray-800"
        >
          {{ headerTitle }}
        </h1>

        <Breadcrumb
          v-if="showBreadcrumb"
          class="hidden tablet-h:block pb-3 tracking-[-0.25px]"
          :items="breadcrumbItems"
          size="lg"
          collapse
          @select="onBreadcrumbSelect"
        />

        <div class="flex items-center gap-[8px] tablet-h:hidden">
          <Button
            variant="primary"
            size="lg"
            :icon="FileAddIcon"
            :disabled="!canStartUpload"
            @click="handleUploadClick"
          >
            Upload
          </Button>
          <Button
            variant="stroke"
            size="lg"
            :icon="FolderAddIcon"
            @click="handleNewFolderClick"
          >
            Folder
          </Button>
          <Button
            v-if="selectedItems.length"
            variant="stroke"
            size="lg"
            :icon="MenuIcon"
            data-keep-selection
            @click="handleMobileThreeDotsClick"
          />
        </div>

        <div class="hidden tablet-h:flex items-center gap-[10px]">
          <Button
            variant="primary"
            :icon="FileAddIcon"
            :disabled="!canStartUpload"
            @click="handleUploadClick"
          >
            Upload
          </Button>
          <Button
            variant="stroke"
            :icon="FolderAddIcon"
            @click="handleNewFolderClick"
          >
            Folder
          </Button>
          <ExplorerSelectionBar
            data-keep-selection
            :selected-items="selectedItems"
            @reset="selected = []"
          />
          <div
            v-if="showOptionsMenu"
            ref="optionsMenuTrigger"
            class="pointer-events-auto!"
            data-keep-selection
          >
            <Button
              variant="stroke"
              :icon="MenuIcon"
              data-keep-selection
              data-testid="file-options-trigger"
              @click="optionsMenuOpen = !optionsMenuOpen"
            />
            <Teleport to="body">
              <Transition v-bind="optionsMenuTransition">
                <div
                  v-if="optionsMenuOpen"
                  ref="optionsMenuPanel"
                  class="z-50"
                  data-keep-selection
                  :style="optionsMenuStyles"
                >
                  <FileContextMenu
                    v-if="isSingleFileSelected"
                    :file="selectedFiles[0]"
                    @clicked="optionsMenuOpen = false"
                  />
                  <MultipleFilesContextMenu
                    v-else-if="isMultipleFilesSelected"
                    :files="selectedFiles"
                    @clicked="optionsMenuOpen = false"
                  />
                </div>
              </Transition>
            </Teleport>
          </div>
        </div>
      </div>

      <div class="flex flex-col items-end">
        <div
          class="hidden tablet-h:block font-primary text-[12px] text-gray-600/60 mb-2 text-right whitespace-nowrap"
        >
          Adding copyright as
        </div>

        <template v-if="explorerStore.selectedCopyrightOwner">
          <Button
            v-if="device.isMobile.value"
            class="size-10! shrink-0 px-2!"
            @click="handleSelectOwnerClick"
          >
            <Avatar
              :src="explorerStore.selectedCopyrightOwner.avatarUrl"
              :size="avatarSize"
            />
          </Button>

          <div v-else ref="ownerMenuTrigger">
            <button
              type="button"
              class="relative flex h-[30px] w-[184px] cursor-pointer items-center rounded-[6px] border border-gray-200 bg-white py-[5px] pl-[10px] pr-[31px] text-left text-[14px] leading-[18px] outline-none transition-colors hover:border-gray-300 focus-visible:border-green-500 focus-visible:ring-2 focus-visible:ring-green-200/50"
              @click="ownerMenuOpen = !ownerMenuOpen"
            >
              <Avatar
                :src="explorerStore.selectedCopyrightOwner.avatarUrl"
                :size="18"
                class="mr-2 shrink-0"
              />
              <span class="min-w-0 flex-1 truncate text-gray-800">
                {{ explorerStore.selectedCopyrightOwner.name }}
              </span>
              <ArrowSelectIcon
                class="pointer-events-none absolute right-[8px] top-1/2 size-[15px] -translate-y-1/2 text-gray-800"
                aria-hidden="true"
              />
            </button>

            <Teleport to="body">
              <Transition v-bind="ownerMenuTransition">
                <div
                  v-if="ownerMenuOpen"
                  ref="ownerMenuPanel"
                  class="z-50"
                  :style="ownerMenuStyles"
                >
                  <Menu v-model="selectedCopyrightOwnerId" select="single">
                    <Menu.Section>Copyright owners</Menu.Section>

                    <div class="w-[270px] max-h-[200px] overflow-auto">
                      <Menu.Item
                        v-for="owner in copyrightOwners"
                        :key="owner.id"
                        :value="owner.id"
                        size="large"
                        :avatar="owner.avatarUrl || undefined"
                        @click="
                          handleCopyrightOwnerSelect(owner, closeOwnerMenu)
                        "
                      >
                        {{ owner.name }}
                        <template v-if="isOrganization(owner)" #description>
                          <span class="flex items-center gap-1.5">
                            <SmallOrganizationIcon class="size-3 shrink-0" />
                            Managed by {{ managedBy(owner) }}
                          </span>
                        </template>
                      </Menu.Item>

                      <div :ref="handleOwnersSentinel" />
                    </div>

                    <Menu.Divider />

                    <Menu.Item
                      size="large"
                      :icon="NewCopyrightOwnerIcon"
                      @click="handleNewCopyrightOwner(closeOwnerMenu)"
                    >
                      New copyright owner
                    </Menu.Item>
                  </Menu>
                </div>
              </Transition>
            </Teleport>
          </div>
        </template>

        <Skeleton
          v-else
          class="w-[42px] tablet-h:w-[184px] h-10 tablet-h:h-8 desktop:h-[30px] rounded-sm"
        />
      </div>
    </div>

    <!-- Table -->
    <ExplorerTable
      ref="tableRef"
      class="-mr-4 tablet-h:mr-0"
      :items="items"
      :loading="loading"
      v-model:selected="selected"
      v-model:sort="sort"
      @scroll-end="fetchNextPage"
    />

    <div
      v-if="showCallout"
      class="w-full absolute bottom-[88px] z-10 px-4 -mx-4 flex justify-center tablet-h:hidden"
    >
      <div
        class="w-full max-w-[404px] rounded-[10px] bg-[#F5F7F9] border border-gray-300/30 px-3 py-2 flex gap-x-3"
      >
        <Pill class="h-fit" variant="success">Pro</Pill>
        <div class="leading-[21px]">
          <div class="font-secondary text-[14px] text-gray-800 inline">
            Add unlimited copyrights plus more features.
          </div>
          <Link icon="arrow" @click="router.push('/upgrade')">
            Upgrade to Pro
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Avatar,
  Breadcrumb,
  Button,
  Link,
  Menu,
  Pill,
  Skeleton,
  type BreadcrumbItem,
  type TableSort,
} from '@dacr/library'
import {
  ArrowSelectIcon,
  FileAddIcon,
  FolderAddIcon,
  MenuIcon,
  AddIcon,
  SmallOrganizationIcon,
} from '@dacr/icons'
import { usePath } from '@/shared/composables/usePath'
import { graphql } from '@/shared/graphql/router'
import { ItemSort } from '@/shared/graphql/router/graphql'
import {
  UserPermission,
  CopyrightOwnerType,
  CopyrightOwnersNewDocument,
  useCopyrightOwnersNewQuery,
  useSelectCopyrightOwnerMutation,
  type CopyrightOwnerNew,
  type CopyrightOwnersNewQuery,
} from '@/shared/graphql/legacy/generated'
import { apolloClient } from '@/shared/graphql/client'
import { useQuery } from '@vue/apollo-composable'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import { useUploadFlow } from '@/features/upload/composables/useUploadFlow'
import { useRoute, useRouter } from 'vue-router'
import {
  computed,
  h,
  onBeforeUnmount,
  ref,
  watch,
  type ComponentPublicInstance,
} from 'vue'
import ExplorerTable from '@/features/explorer/components/ExplorerTable.vue'
import ExplorerSelectionBar from '@/features/explorer/components/ExplorerSelectionBar.vue'
import { useExplorerStore } from '@/features/explorer/store'
import { useExplorerSelection } from '@/features/explorer/composables/useExplorerSelection'
import FileContextMenu from '@/features/explorer/components/FileContextMenu.vue'
import MultipleFilesContextMenu from '@/features/explorer/components/MultipleFilesContextMenu.vue'
import { useUserStore } from '@/shared/stores/user'
import { useHead } from '@unhead/vue'
import { useDevice } from '@/shared/composables/useDevice'
import { sortExplorerItems } from '@/features/explorer/utils/sortExplorerItems'
import {
  mergeLocalExplorerItems,
  pruneLocalExplorerItems,
  filterPendingRemovalItems,
  prunePendingRemovalItems,
} from '@/features/explorer/utils/localExplorerItems'
import { useBillingStore } from '@/features/billing/store'
import UpsellBanner from '@/features/billing/components/UpsellBanner.vue'
import AlertBanner from '@/shared/components/AlertBanner.vue'
import type { Item } from '@/shared/graphql/router/graphql'
import { isFile } from '@/features/explorer/utils/itemGuards'
import { shouldPreserveExplorerSelection } from '@/features/explorer/utils/preserveSelection'
import { useFloatingMenu } from '@/shared/composables/useFloatingMenu'

const FOLDER_BY_PATH_QUERY = graphql(`
  query FolderByPath(
    $path: String!
    $sort: ItemSort
    $first: Int
    $after: String
  ) {
    folderByPath(path: $path) {
      id
      name
      path
      createdAt
      totalFiles
      items(input: { sort: $sort, first: $first, after: $after }) {
        edges {
          cursor
          node {
            ... on File {
              id
              name
              type
              description
              role
              permissions
              copyright {
                id
                status
                registrations {
                  certificateUrl
                  expiresAt
                  origin {
                    id
                    name
                    flag {
                      url
                    }
                  }
                  timestamp
                }
              }
              metadata {
                originalFilename
                size
              }
              asset {
                previewImageUrl
              }
              settings {
                isPrivate
              }
              registrant {
                id
                name
              }
              owner {
                ... on User {
                  id
                  name
                  avatar {
                    url
                  }
                }
                ... on Organization {
                  id
                  name
                  avatar {
                    url
                  }
                }
              }
              coOwners(input: { first: 3, filterBy: ACCEPTED }) {
                totalCount
                edges {
                  node {
                    ... on EntityWithRole {
                      entity {
                        ... on User {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                        ... on Organization {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                      }
                      role
                    }
                  }
                }
              }
              authors(input: { first: 3, filterBy: ACCEPTED }) {
                totalCount
                edges {
                  node {
                    ... on EntityWithRole {
                      entity {
                        ... on User {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                        ... on Organization {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                      }
                      role
                    }
                  }
                }
              }
              viewers(input: { first: 10 }) {
                totalCount
                edges {
                  node {
                    ... on ViewerEntity {
                      entity {
                        ... on User {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                        ... on Organization {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on Folder {
              id
              name
              path
              createdAt
              totalFiles
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        totalFiles
      }
    }
  }
`)

type OwnerItem = CopyrightOwnersNewQuery['copyrightOwnersNew']['items'][number]

const {
  result: copyrightOwnersQuery,
  loading: copyrightOwnersLoading,
  fetchMore: fetchMoreOwners,
} = useCopyrightOwnersNewQuery()
const { mutate: selectCopyrightOwner } = useSelectCopyrightOwnerMutation()

const device = useDevice()
const route = useRoute()
const path = usePath()
const router = useRouter()

const { selectedIds: selected, clear: clearSelection } = useExplorerSelection()

const billingStore = useBillingStore()
const explorerStore = useExplorerStore()
const {
  showUploadDialog,
  uploadFiles,
  hasUploadPermission: canUpload,
  canStartUpload,
} = useUploadFlow()
const userStore = useUserStore()
const selectedCopyrightOwnerId = computed({
  get: () => explorerStore.selectedCopyrightOwner?.id ?? '',
  set: () => {},
})

const dragging = ref(false)
const tableRef = ref<InstanceType<typeof ExplorerTable>>()

const showCallout = computed(
  () =>
    !userStore.user?.permissions.includes(UserPermission.Pro) &&
    explorerStore.currentFolder?.items.filter(isFile).length
)

const copyrightOwners = computed(
  () => copyrightOwnersQuery.value?.copyrightOwnersNew.items
)

const avatarSize = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 24 : 18
)

const overlayClasses = computed(() => ({
  ...(dragging.value &&
    !loading.value &&
    canUpload.value && {
      'border-2': true,
      'border-dashed': true,
      'border-primary': true,
    }),
}))

const showBreadcrumb = computed(() =>
  device.isMobile.value || device.isTabletVertical.value
    ? path.length > 1
    : true
)

const headerTitle = computed(() => {
  const folderName = (route.params.path as string[] | undefined)?.at(-1)
  return folderName || 'My Copyrights'
})

const breadcrumbItems = computed<BreadcrumbItem[]>(() =>
  path.map((label) => ({ label }))
)

const onBreadcrumbSelect = (_item: BreadcrumbItem, index: number) => {
  if (index === 0) {
    router.push({ name: 'FileExplorerRootFolderView' })
  } else {
    router.push({
      name: 'FileExplorerFolderView',
      params: { path: path.slice(1, index + 1) },
    })
  }
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const tableEl = tableRef.value?.$el
  if (shouldPreserveExplorerSelection(target)) return

  if (tableEl && !tableEl.contains(target)) {
    selected.value = []
  }
}

const ownerMenuOpen = ref(false)

const {
  reference: ownerMenuTrigger,
  floating: ownerMenuPanel,
  floatingStyles: ownerMenuStyles,
  transition: ownerMenuTransition,
} = useFloatingMenu(ownerMenuOpen, {
  placement: 'bottom-end',
  matchWidth: false,
})

const closeOwnerMenu = () => {
  ownerMenuOpen.value = false
}

// Get explorer items
const sortBy = ref<ItemSort>(explorerStore.sortBy)

const ITEM_SORT_MAP: Record<string, ItemSort> = {
  'name:asc': ItemSort.NameAsc,
  'name:desc': ItemSort.NameDesc,
  'timestamp:asc': ItemSort.TimestampAsc,
  'timestamp:desc': ItemSort.TimestampDesc,
}

const TABLE_SORT_MAP: Record<ItemSort, TableSort> = {
  [ItemSort.NameAsc]: { field: 'name', direction: 'asc' },
  [ItemSort.NameDesc]: { field: 'name', direction: 'desc' },
  [ItemSort.TimestampAsc]: { field: 'timestamp', direction: 'asc' },
  [ItemSort.TimestampDesc]: { field: 'timestamp', direction: 'desc' },
}

const sort = ref<TableSort | null>(TABLE_SORT_MAP[explorerStore.sortBy])

watch(sort, (value) => {
  if (!value) return

  const next = ITEM_SORT_MAP[`${value.field}:${value.direction}`]
  if (!next) return

  sortBy.value = next
  explorerStore.setSort(sortBy.value)
})

const folderPath = computed(() => {
  const segments = route.params.path as string[] | undefined
  return segments?.length ? `/${segments.join('/')}` : '/'
})

const variables = computed(() => ({
  path: folderPath.value,
  sort: sortBy.value,
  first: 50,
}))

const { result, loading, fetchMore } = useQuery(
  FOLDER_BY_PATH_QUERY,
  variables,
  {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    pollInterval: 10_000,
    clientId: 'router',
  }
)

const extractItems = (data: typeof result.value): Item[] => {
  if (!data?.folderByPath?.items?.edges) return []
  return data.folderByPath.items.edges.map((edge) => edge.node as Item)
}

watch(
  result,
  () => {
    if (!result.value) return

    const folderData = result.value.folderByPath
    if (!folderData) return

    const folderItems = extractItems(result.value)
    pruneLocalExplorerItems(folderData.id, folderItems)
    prunePendingRemovalItems(folderData.id, folderItems)

    explorerStore.setCurrentFolder({
      id: folderData.id,
      name: folderData.name,
      path: folderData.path,
      createdAt: folderPath.value === '/' ? null : folderData.createdAt,
      totalFiles: folderData.totalFiles,
      items: folderItems,
      sortBy: sortBy.value,
      endCursor: folderData.items.pageInfo.endCursor,
      hasNextPage: folderData.items.pageInfo.hasNextPage,
    })
  },
  { immediate: true }
)

watch(
  copyrightOwners,
  () => {
    if (!copyrightOwners.value?.length) return

    const primaryOwner = copyrightOwners.value.find((owner) => owner.isSelected)
    if (!primaryOwner) return

    explorerStore.setCopyrightOwner(primaryOwner as CopyrightOwnerNew)
  },
  { immediate: true }
)

watch(folderPath, () => {
  clearSelection()
})

const items = computed(() => {
  if (loading.value && !explorerStore.currentFolder?.hasNextPage)
    return undefined

  const folderItems = extractItems(result.value)
  const folderId =
    result.value?.folderByPath?.id ?? explorerStore.currentFolder?.id
  const items = folderId
    ? filterPendingRemovalItems(
        folderId,
        mergeLocalExplorerItems(folderId, folderItems)
      )
    : folderItems

  return sortExplorerItems(items) as Item[]
})

const title = computed(() => {
  const segments = route.params.path as string[] | undefined
  return `${segments?.at(-1) || 'Home'} - Dacr`
})
useHead({ title })

const selectedItems = computed(() =>
  (items.value || []).filter((item) => selected.value.includes(item.id))
)

const selectedFiles = computed(() => selectedItems.value.filter(isFile))

const areAllSelectedRowsFiles = computed(
  () => selectedItems.value.length > 0 && selectedItems.value.every(isFile)
)

const showOptionsMenu = computed(() => areAllSelectedRowsFiles.value)

const isSingleFileSelected = computed(
  () => areAllSelectedRowsFiles.value && selected.value.length === 1
)

const isMultipleFilesSelected = computed(
  () => areAllSelectedRowsFiles.value && selected.value.length > 1
)

const optionsMenuOpen = ref(false)

const {
  reference: optionsMenuTrigger,
  floating: optionsMenuPanel,
  floatingStyles: optionsMenuStyles,
  transition: optionsMenuTransition,
} = useFloatingMenu(optionsMenuOpen, {
  placement: 'bottom-end',
  matchWidth: false,
})

// Drag & drop
const handleDragOver = () => {
  if (loading.value || !canUpload.value) return
  dragging.value = true
}

const handleDragLeave = () => {
  dragging.value = false
}

const handleDrop = (event: DragEvent) => {
  if (loading.value || !canUpload.value) return

  dragging.value = false

  const files = event.dataTransfer?.files
  if (!files?.length) return

  uploadFiles(files)
}

const handleUploadClick = () => {
  showUploadDialog()
}

const handleNewFolderClick = () => {
  openDialog(Dialog.AddFolder, {
    props: { folderId: result.value?.folderByPath?.id },
  })
}

const handleMobileThreeDotsClick = () => {
  openSheet(Sheets.ThreeDotsMenu, {
    props: { items: selectedItems.value },
  })
}

const handleSelectOwnerClick = () => {
  openSheet(Sheets.SelectCopyrightOwner)
}

const isOrganization = (owner: OwnerItem) =>
  owner.type === CopyrightOwnerType.Organization

const managedBy = (owner: OwnerItem) =>
  owner.owner && 'name' in owner.owner ? owner.owner.name : ''

const handleCopyrightOwnerSelect = (owner: OwnerItem, close: () => void) => {
  close()

  apolloClient.cache.updateQuery<CopyrightOwnersNewQuery>(
    { query: CopyrightOwnersNewDocument },
    (response) => {
      if (!response) return

      return {
        ...response,
        copyrightOwnersNew: {
          ...response.copyrightOwnersNew,
          items: response.copyrightOwnersNew.items.map((item) => ({
            ...item,
            isSelected: item.id === owner.id,
          })),
        },
      }
    }
  )

  selectCopyrightOwner({ id: owner.id })
}

const handleNewCopyrightOwner = (close: () => void) => {
  close()
  openDialog(Dialog.NewCopyrightOwner)
}

const NewCopyrightOwnerIcon = () =>
  h(
    'span',
    {
      class:
        'flex size-6 items-center justify-center rounded-full bg-green-200/50',
    },
    h(AddIcon, { class: 'size-3 text-green-700' })
  )

let ownersObserver: IntersectionObserver | null = null

const handleOwnersSentinel = (el: Element | ComponentPublicInstance | null) => {
  ownersObserver?.disconnect()
  ownersObserver = null

  if (el instanceof Element) {
    ownersObserver = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) fetchNextPageOfOwners()
    })
    ownersObserver.observe(el)
  }
}

// Pagination
const fetchNextPage = () => {
  const folder = explorerStore.currentFolder
  if (folder?.hasNextPage && folder.endCursor && !loading.value) {
    fetchMore({
      variables: {
        after: folder.endCursor,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        const prevEdges = previousData.folderByPath.items.edges
        const newEdges = newData.folderByPath.items.edges

        if (!newEdges.length) return previousData

        return {
          ...newData,
          folderByPath: {
            ...newData.folderByPath,
            items: {
              ...newData.folderByPath.items,
              edges: [...prevEdges, ...newEdges],
            },
          },
        }
      },
    })
  }
}

// Copyright owners pagination
const fetchNextPageOfOwners = () => {
  if (
    copyrightOwnersQuery.value?.copyrightOwnersNew.next &&
    !copyrightOwnersLoading.value
  ) {
    fetchMoreOwners({
      variables: {
        next: copyrightOwnersQuery.value.copyrightOwnersNew.next,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          ...newData,
          copyrightOwnersNew: {
            ...newData.copyrightOwnersNew,
            items: [
              ...previousData.copyrightOwnersNew.items,
              ...newData.copyrightOwnersNew.items,
            ],
          },
        }
      },
    })
  }
}

onBeforeUnmount(() => {
  ownersObserver?.disconnect()
  clearSelection()
  explorerStore.reset()
})
</script>
